/**
 * `VITE_AUTH_MODE=manual` — no issuer, paste a token.
 *
 * This exists for exactly two situations, both real:
 *
 *   * a service running with `INVENTORY_AUTH_DEV_LOCAL_JWT=1`, which verifies a
 *     locally-signed HS256 token so the whole stack runs with no identity provider;
 *   * `scripts/demo_slice9.py`, which generates an RS256 keypair in-process and injects
 *     the key set, so nothing reaches auth.xpertte.com — a demo that needed the internet
 *     would be one nobody could run on a train.
 *
 * It is not a login form. It does not send a password anywhere, it mints nothing, and it
 * cannot make an unacceptable token acceptable: the service still verifies the signature,
 * the audience, the issuer and the required claims. All this does is put a credential
 * somebody already has into the `Authorization` header.
 */

import { Alert, Box, Button, Paper, Stack, TextField, Typography } from '@mui/material';
import { useState } from 'react';

import { useAuth } from '../auth/context';
import { decodeToken, isExpired, readTenantClaim } from '../auth/token';

export function ManualTokenPanel() {
  const { acceptToken } = useAuth();
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  const submit = () => {
    const token = value.trim();
    if (!token) {
      setError('Paste a token first.');
      return;
    }
    const claims = decodeToken(token);
    if (!claims) {
      setError('That does not decode as a JWT. Three dot-separated base64url segments.');
      return;
    }
    if (isExpired(token, 0)) {
      setError('That token is already past its `exp`. The service would refuse it.');
      return;
    }
    const tenant = readTenantClaim(claims);
    if (!tenant.name) {
      // Not refused — the service is the authority and a 403 from it is a more useful
      // thing to see than a guess from here. But saying it now saves a round trip.
      setError(
        'Warning: this token carries none of org, org_id, tenant or tenant_id, so the ' +
          'service will answer 403 tenant_claim_missing. Submitting anyway will show you ' +
          'exactly that.',
      );
    }
    acceptToken(token);
  };

  return (
    <Box sx={{ maxWidth: 720, mx: 'auto', mt: 6, px: 3, pb: 6 }}>
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
        <Typography sx={{ fontSize: 18, fontWeight: 800, mb: 0.5 }}>
          Inai Inventory
        </Typography>
        <Typography color="text.secondary" sx={{ fontSize: 13, mb: 2.5 }}>
          This shell is in <strong>manual token mode</strong>. There is no identity
          provider configured, so nothing will be redirected anywhere — paste an access
          token for the service to verify.
        </Typography>

        <TextField
          label="Access token"
          value={value}
          onChange={(event) => {
            setValue(event.target.value);
            setError(null);
          }}
          multiline
          minRows={4}
          fullWidth
          spellCheck={false}
          sx={{ '& textarea': { fontFamily: 'ui-monospace, monospace', fontSize: 12 } }}
        />

        {error ? (
          <Alert severity="warning" variant="outlined" sx={{ mt: 2, fontSize: 13 }}>
            {error}
          </Alert>
        ) : null}

        <Stack direction="row" spacing={1} sx={{ mt: 2.5 }}>
          <Button variant="contained" onClick={submit}>
            Use this token
          </Button>
        </Stack>

        <Typography color="text.secondary" sx={{ fontSize: 12, mt: 2.5 }}>
          Kept in <code>sessionStorage</code> and dropped when the tab closes. The service
          verifies it on every request; this shell verifies nothing.
        </Typography>
      </Paper>
    </Box>
  );
}
